<script setup lang="ts">
import { defineProps, onMounted, ref, watch } from "vue";
import SidebarMenu from "@/components/SidebarMenu.vue";
import TagSidebarItems from "@/components/sidebar/TagSidebarItems.vue";
import PageHeader from "@/components/headers/PageHeader.vue";
import { useBlogsStore } from "@/stores/blogsStore";
import BlogSidebar from "@/views/Blog/components/BlogSidebar.vue";
import BlogContent from "@/views/Blog/components/BlogContent.vue";
import BlogFooter from "@/views/Blog/components/BlogFooter.vue";
import PageFooter from "@/components/PageFooter.vue";
import { useLangStore } from "@/stores/langStore";
import { useSelectBlogTag } from "@/utils/useSelectBlogTag";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useSafeBack } from "@/utils/useSafeBack";

const props = defineProps<{
  id: string;
}>();

const languageStore = useLangStore();
const blogStore = useBlogsStore();
const blogsCardsStore = useBlogsStore();
const { selectTag } = useSelectBlogTag();
const { safeBack } = useSafeBack();

const scrollContainer = ref<HTMLElement | null>(null);
const isMenuOpen = ref(false);

onMounted(() => {
  blogsCardsStore.loadBlogDetail(props.id);
});

watch(
  () => props.id,
  (newId) => {
    blogsCardsStore.loadBlogDetail(newId);
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = 0;
    }
  }
);

function closeBlog() {
  blogStore.currentBlogDetailId = null;
  safeBack(ROUTE_NAMES.BLOG_LIST);
}
</script>

<template>
  <div class="flex flex-col xl:flex-row h-full xl:h-screen bg-secondary">
    <SidebarMenu
      :show-blog-btn="false"
      :is-menu-open="isMenuOpen"
      @toggle-menu="isMenuOpen = !isMenuOpen"
      class="sticky top-0"
    >
      <TagSidebarItems @close-menu="isMenuOpen = false" />
    </SidebarMenu>

    <main
      class="flex-1 grid min-h-0 grid-rows-[auto,1fr] bg-secondary"
      v-if="blogsCardsStore.blogDetail"
    >
      <PageHeader @close="closeBlog">
        <template #header-content>
          <div class="flex gap-2 pl-5">
            <div
              v-for="tag in blogsCardsStore.blogDetail.tags"
              :key="tag"
              class="flex items-center h-[17px] md:h-[25px] xl:h-8 bg-ternary text-brown sf-pro font-semibold text-[11px] leading-100p md:text-sm md:leading-[21px] xl:text-base xl:leading-[24px] px-2 py-[2px] blu-[30px] rounded-lg tracking-normal cursor-pointer"
              @click="selectTag(tag.id)"
            >
              {{
                tag[languageStore.current] || tag[languageStore.fallbackLang]
              }}
            </div>
          </div>
        </template>
      </PageHeader>

      <div
        class="xl:grid xl:grid-cols-[1fr,427px] min-h-0 bg-secondary pt-12 xl:pt-0"
      >
        <div class="overflow-y-auto min-h-0 no-scrollbar" ref="scrollContainer">
          <BlogContent :blog="blogsCardsStore.blogDetail" />
          <BlogSidebar
            class="xl:hidden"
            :author="blogsCardsStore.blogDetail.author"
            :related-blogs="blogsCardsStore.blogDetail.related_blogs"
          />
          <BlogFooter
            :related-blogs="blogsCardsStore.blogDetail.related_blogs"
          />
        </div>
        <BlogSidebar
          class="hidden xl:block"
          :author="blogsCardsStore.blogDetail.author"
          :related-blogs="blogsCardsStore.blogDetail.related_blogs"
        />
      </div>
      <div class="fixed bottom-0 left-0 w-full">
        <PageFooter />
      </div>
    </main>
  </div>
</template>
